import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, MapPinIcon, CalendarIcon, ClockIcon, TrendingUpIcon, FilterIcon, BookmarkIcon, ExternalLinkIcon, UsersIcon, PlusCircleIcon, UserPlusIcon, Share2Icon, XIcon, DollarSignIcon, TicketIcon, BarChartIcon, FacebookIcon, TwitterIcon, LinkIcon, CheckIcon } from 'lucide-react';
import { DropdownMenu } from '../components/DropdownMenu';
import { useAppStore } from '../store';
import EmptyState from '../components/EmptyState';
const Events = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    category: '',
    ticketPrice: '',
    maxAttendees: ''
  });
  const [linkCopied, setLinkCopied] = useState(false);
  // Use global state
  const {
    savedEvents,
    saveEvent,
    unsaveEvent
  } = useAppStore();
  const categories = [{
    id: 'all',
    name: 'All Events',
    count: 245
  }, {
    id: 'entertainment',
    name: 'Entertainment',
    count: 37
  }, {
    id: 'business',
    name: 'Business & Networking',
    count: 85
  }, {
    id: 'health',
    name: 'Health & Fitness',
    count: 53
  }, {
    id: 'education',
    name: 'Education',
    count: 27
  }, {
    id: 'arts',
    name: 'Arts & Culture',
    count: 43
  }];
  const locations = ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Kikuyu', 'Kiserian'];
  const eventTypes = [{
    id: 'physical',
    name: 'Physical'
  }, {
    id: 'online',
    name: 'Online'
  }, {
    id: 'hybrid',
    name: 'Hybrid'
  }];
  const trendingEvents = [{
    id: '1',
    title: 'Venture Wars',
    category: 'Business',
    type: 'Physical',
    venue: 'Coworking',
    description: "Startup pitch competition featuring Kenya's most promising entrepreneurs competing for funding.",
    date: 'Wed, Aug 20, 2025',
    time: '3:00 PM',
    location: 'Arifa Gallery, Nairobi',
    price: 'Free. Check event page',
    tags: ['Startup', 'Investment', 'Networking'],
    trending: true,
    verified: true,
    ticketUrl: 'https://eventbrite.com/venture-wars',
    organizer: 'Eventbrite'
  }, {
    id: '2',
    title: 'Yoga In The Park',
    category: 'Fitness',
    type: 'Physical',
    venue: 'Karuraforest',
    description: 'Yoga in the Park Nairobi - Sitatunga Trail. Relax and rejuvenate in nature with guided yoga sessions.',
    date: 'Sat, Aug 10, 2025',
    time: '7:00 AM',
    location: 'T-Work Consulting Nairobi, Nairobi',
    price: 'Free',
    tags: ['Yoga', 'Wellness', 'Outdoor'],
    trending: true,
    verified: true,
    ticketUrl: 'https://kenyabuzz.com/yoga-in-the-park',
    organizer: 'KenyaBuzz'
  }, {
    id: '3',
    title: 'tabOO Thursday',
    category: 'Entertainment',
    type: 'Physical',
    venue: 'Tribeka',
    description: 'Weekly entertainment night featuring music, dancing, and socializing at Babylon Social.',
    date: 'Thu, Aug 8, 2025',
    time: '10:00 AM',
    location: 'Babylon Social Club, Nairobi',
    price: 'KES 1,500',
    tags: ['Party', 'Music', 'Nightlife'],
    trending: true,
    verified: false,
    ticketUrl: 'https://ticketsasa.com/taboo-thursday',
    organizer: 'TicketSasa'
  }, {
    id: '4',
    title: 'Art In Motion Live',
    category: 'Arts',
    type: 'Physical',
    venue: 'Exhibition',
    description: 'Live performance art showcase featuring contemporary Kenyan artists and dancers.',
    date: 'Fri, Aug 22, 2025',
    time: '7:00 PM',
    location: 'Kenya National Theatre, Nairobi',
    price: 'KES 1,000',
    tags: ['Performance Art', 'Dance', 'Live Show'],
    trending: true,
    verified: true,
    ticketUrl: 'https://ticketsasa.com/art-in-motion',
    organizer: 'TicketSasa'
  }];
  const upcomingEvents = [{
    id: '5',
    title: 'Career Intelligence and Visibility Info Session',
    category: 'Business',
    type: 'Physical',
    venue: 'Conference',
    description: 'Professional development session focused on career advancement and personal branding.',
    date: 'Fri, Sep 5, 2025',
    time: '7:00 AM',
    location: 'The Nairobi Club, Nairobi',
    price: 'Free. Check event page',
    tags: ['Career', 'Professional Development', 'Networking'],
    trending: true,
    verified: true,
    ticketUrl: 'https://eventbrite.com/career-intelligence',
    organizer: 'Eventbrite'
  }];
  const toggleSaveEvent = (eventId: string) => {
    if (savedEvents.includes(eventId)) {
      unsaveEvent(eventId);
    } else {
      saveEvent(eventId);
    }
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };
  const addToCalendar = (event: any, service: string) => {
    const title = encodeURIComponent(event.title);
    const details = encodeURIComponent(event.description);
    const location = encodeURIComponent(event.location);
    const dates = '20250820T090000Z/20250820T110000Z'; // Example format
    let url = '';
    switch (service) {
      case 'google':
        url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&location=${location}&dates=${dates}`;
        break;
      case 'outlook':
        url = `https://outlook.office.com/calendar/0/deeplink/compose?subject=${title}&body=${details}&location=${location}&startdt=2025-08-20T09:00:00&enddt=2025-08-20T11:00:00`;
        break;
      case 'yahoo':
        url = `https://calendar.yahoo.com/?v=60&title=${title}&desc=${details}&in_loc=${location}&st=20250820T090000Z&et=20250820T110000Z`;
        break;
      case 'ical':
        // For iCal we would typically generate a file download
        alert('iCal download functionality would be implemented here');
        return;
    }
    window.open(url, '_blank');
  };
  const handleCreateEventSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Event creation request submitted! Our team will review it shortly.');
    setShowCreateEventModal(false);
    setEventFormData({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      category: '',
      ticketPrice: '',
      maxAttendees: ''
    });
  };
  const handleEventFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setEventFormData({
      ...eventFormData,
      [name]: value
    });
  };
  // Demo mode: show empty state if query param is present
  const urlParams = new URLSearchParams(window.location.search);
  const showEmptyState = urlParams.get('empty') === 'true';
  return <div className="pb-16" data-id="element-1622">
      <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-1623">
        <div className="flex max-w-4xl mx-auto" data-id="element-1624">
          <button className={`flex-1 py-3 text-center ${activeTab === 'events' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'} transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700`} onClick={() => setActiveTab('events')} data-id="element-1625">
            <div className="flex justify-center items-center" data-id="element-1626">
              <CalendarIcon className="w-4 h-4 mr-2" data-id="element-1627" />
              Events & Shows
            </div>
          </button>
          <button className={`flex-1 py-3 text-center ${activeTab === 'communities' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'} transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700`} onClick={() => setActiveTab('communities')} data-id="element-1628">
            <div className="flex justify-center items-center" data-id="element-1629">
              <UsersIcon className="w-4 h-4 mr-2" data-id="element-1630" />
              Communities
            </div>
          </button>
          <button className={`flex-1 py-3 text-center ${activeTab === 'monetization' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'} transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700`} onClick={() => setActiveTab('monetization')} data-id="element-1631">
            <div className="flex justify-center items-center" data-id="element-1632">
              <TicketIcon className="w-4 h-4 mr-2" data-id="element-1633" />
              Event Solutions
            </div>
          </button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-4" data-id="element-1634">
        {activeTab === 'events' && <>
            <div className="flex justify-between items-center mb-4" data-id="element-1635">
              <div className="relative flex-1 mr-2" data-id="element-1636">
                <input type="text" placeholder="Search events by name, category, or tags..." className="w-full p-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" data-id="element-1637" />
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1638" />
              </div>
              <Link to="/seller-info" className="flex items-center bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-105" data-id="element-1639">
                <PlusCircleIcon className="w-4 h-4 mr-1" data-id="element-1640" />
                Create Event with us
              </Link>
            </div>
            <div className="flex justify-between items-center mb-4" data-id="element-1641">
              <div className="flex items-center" data-id="element-1642">
                <button className="flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-full bg-gray-50 dark:bg-gray-700 text-sm mr-2 dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-600" data-id="element-1643">
                  <MapPinIcon className="w-4 h-4 mr-1" data-id="element-1644" />
                  Location
                </button>
                <button className="flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-full bg-gray-50 dark:bg-gray-700 text-sm dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-600" data-id="element-1645">
                  <FilterIcon className="w-4 h-4 mr-1" data-id="element-1646" />
                  Filters
                </button>
              </div>
              <Link to="/tickets" className="text-sm underline dark:text-gray-200 transition-colors duration-300 hover:text-gray-800 dark:hover:text-gray-100" data-id="element-1647">
                View My Tickets
              </Link>
            </div>
            <div className="mb-6" data-id="element-1648">
              <h3 className="text-sm font-medium mb-2 dark:text-gray-200" data-id="element-1649">
                Event Categories
              </h3>
              <div className="flex overflow-x-auto pb-2 space-x-2" data-id="element-1650">
                {categories.map(category => <button key={category.id} className={`flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-full whitespace-nowrap text-sm ${category.id === 'all' ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800' : 'bg-white dark:bg-gray-800 dark:text-gray-200'} transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700 transform hover:scale-105`} data-id="element-1651">
                    {category.id === 'all' && <span className="w-2 h-2 bg-white dark:bg-gray-800 rounded-full mr-1" data-id="element-1652"></span>}
                    {category.name}{' '}
                    <span className="ml-1 text-xs" data-id="element-1653">{category.count}</span>
                  </button>)}
              </div>
            </div>
            <div className="flex justify-between mb-4" data-id="element-1654">
              <div data-id="element-1655">
                <h3 className="text-sm font-medium mb-2 dark:text-gray-200" data-id="element-1656">
                  Event Type
                </h3>
                <div className="flex space-x-2" data-id="element-1657">
                  {eventTypes.map(type => <button key={type.id} className={`px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm ${type.id === 'physical' ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800' : 'bg-white dark:bg-gray-800 dark:text-gray-200'} transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700 transform hover:scale-105`} data-id="element-1658">
                      {type.name}
                    </button>)}
                </div>
              </div>
              <div data-id="element-1659">
                <h3 className="text-sm font-medium mb-2 dark:text-gray-200" data-id="element-1660">
                  Popular Locations
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1661">
                  {locations.map((location, index) => <button key={index} className={`px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs ${location === 'Nairobi' ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800' : 'bg-white dark:bg-gray-800 dark:text-gray-200'} transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700 transform hover:scale-105`} data-id="element-1662">
                      {location}
                    </button>)}
                </div>
              </div>
            </div>
            {showEmptyState ? <EmptyState title="No Events Found" description="There are no events matching your criteria. Try adjusting your filters or create your own event!" icon={<CalendarIcon className="w-10 h-10 text-gray-500 dark:text-gray-400" data-id="element-1664" />} actionText="Create Event" actionLink="/seller-info" secondaryActionText="Clear Filters" onSecondaryAction={() => window.location.href = '/events'} data-id="element-1663" /> : <>
                <div className="mb-8" data-id="element-1665">
                  <div className="flex items-center mb-4" data-id="element-1666">
                    <CalendarIcon className="w-5 h-5 mr-2 dark:text-gray-200" data-id="element-1667" />
                    <h2 className="text-xl font-bold dark:text-gray-200" data-id="element-1668">
                      Trending Events
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1669">
                    {trendingEvents.map(event => <div key={event.id} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md" data-id="element-1670">
                        <div className="p-4" data-id="element-1671">
                          <div className="flex justify-between items-start mb-2" data-id="element-1672">
                            <h3 className="font-bold dark:text-gray-200" data-id="element-1673">
                              {event.title}
                            </h3>
                            <div className="flex space-x-1" data-id="element-1674">
                              {event.verified && <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md text-xs" data-id="element-1675">
                                  Verified
                                </span>}
                              {event.trending && <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md text-xs" data-id="element-1676">
                                  Trending
                                </span>}
                              <button onClick={() => toggleSaveEvent(event.id)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 transform hover:scale-110" data-id="element-1677">
                                <BookmarkIcon className={`w-4 h-4 ${savedEvents.includes(event.id) ? 'fill-gray-700 dark:fill-gray-300 text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'} transition-colors duration-300`} data-id="element-1678" />
                              </button>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 mb-2" data-id="element-1679">
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-xs dark:text-gray-200" data-id="element-1680">
                              {event.category}
                            </span>
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-xs dark:text-gray-200" data-id="element-1681">
                              {event.type}
                            </span>
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-xs dark:text-gray-200" data-id="element-1682">
                              {event.venue}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1683">
                            {event.description}
                          </p>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mb-1" data-id="element-1684">
                            <CalendarIcon className="w-4 h-4 mr-1" data-id="element-1685" />
                            <span data-id="element-1686">{event.date}</span>
                            <span className="mx-2" data-id="element-1687">•</span>
                            <ClockIcon className="w-4 h-4 mr-1" data-id="element-1688" />
                            <span data-id="element-1689">{event.time}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1690">
                            <MapPinIcon className="w-4 h-4 mr-1" data-id="element-1691" />
                            <span data-id="element-1692">{event.location}</span>
                          </div>
                          <div className="text-sm mb-3 flex items-center justify-between" data-id="element-1693">
                            <div data-id="element-1694">
                              <span className="font-medium dark:text-gray-200" data-id="element-1695">
                                Price:{' '}
                              </span>
                              <span className="text-gray-600 dark:text-gray-300" data-id="element-1696">
                                {event.price}
                              </span>
                            </div>
                            {event.price !== 'Free' && event.price !== 'Free. Check event page' && <Link to="/checkout" className="px-3 py-1 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md text-sm transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-105" data-id="element-1697">
                                  Buy Ticket
                                </Link>}
                          </div>
                          <div className="flex flex-wrap gap-1 mb-3" data-id="element-1698">
                            {event.tags.map((tag, index) => <span key={index} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-xs dark:text-gray-200" data-id="element-1699">
                                {tag}
                              </span>)}
                          </div>
                          <div className="flex justify-between items-center" data-id="element-1700">
                            <div className="flex space-x-2" data-id="element-1701">
                              <Link to={`/event/${event.id}`} className="px-3 py-1 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md text-sm transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-105" data-id="element-1702">
                                View Details
                              </Link>
                              <DropdownMenu trigger={<button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm flex items-center dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1704">
                                    <CalendarIcon className="w-3 h-3 mr-1" data-id="element-1705" />
                                    Add to Calendar
                                  </button>} data-id="element-1703">
                                <DropdownMenu.Item label="Google Calendar" onClick={() => addToCalendar(event, 'google')} data-id="element-1706" />
                                <DropdownMenu.Item label="Outlook" onClick={() => addToCalendar(event, 'outlook')} data-id="element-1707" />
                                <DropdownMenu.Item label="Yahoo Calendar" onClick={() => addToCalendar(event, 'yahoo')} data-id="element-1708" />
                                <DropdownMenu.Item label="iCal Download" onClick={() => addToCalendar(event, 'ical')} data-id="element-1709" />
                              </DropdownMenu>
                              <DropdownMenu trigger={<button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1711">
                                    <Share2Icon className="w-3 h-3" data-id="element-1712" />
                                  </button>} data-id="element-1710">
                                <DropdownMenu.Item label="Share on Twitter" icon={<TwitterIcon className="w-4 h-4" data-id="element-1714" />} onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${event.title} on JoinToday.co.ke!`)}&url=${encodeURIComponent(`https://jointoday.co.ke/event/${event.id}`)}`, '_blank')} data-id="element-1713" />
                                <DropdownMenu.Item label="Share on Facebook" icon={<FacebookIcon className="w-4 h-4" data-id="element-1716" />} onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://jointoday.co.ke/event/${event.id}`)}&quote=${encodeURIComponent(`Check out ${event.title} on JoinToday.co.ke!`)}`, '_blank')} data-id="element-1715" />
                                <DropdownMenu.Item label="Share on WhatsApp" icon={<span className="text-sm" data-id="element-1718">📱</span>} onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${event.title} on JoinToday.co.ke! https://jointoday.co.ke/event/${event.id}`)}`, '_blank')} data-id="element-1717" />
                                <DropdownMenu.Item label="Copy Link" icon={<LinkIcon className="w-4 h-4" data-id="element-1720" />} onClick={() => copyToClipboard(`https://jointoday.co.ke/event/${event.id}`)} data-id="element-1719" />
                              </DropdownMenu>
                            </div>
                            <a href={event.ticketUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-300" data-id="element-1721">
                              <span className="mr-1" data-id="element-1722">
                                via {event.organizer}
                              </span>
                              <ExternalLinkIcon className="w-3 h-3" data-id="element-1723" />
                            </a>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
                <div data-id="element-1724">
                  <div className="flex items-center mb-4" data-id="element-1725">
                    <CalendarIcon className="w-5 h-5 mr-2 dark:text-gray-200" data-id="element-1726" />
                    <h2 className="text-xl font-bold dark:text-gray-200" data-id="element-1727">
                      Upcoming Events
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1728">
                    {[...trendingEvents.slice(0, 2), ...upcomingEvents].map(event => <div key={event.id} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md" data-id="element-1729">
                          <div className="p-4" data-id="element-1730">
                            <div className="flex justify-between items-start mb-2" data-id="element-1731">
                              <h3 className="font-bold dark:text-gray-200" data-id="element-1732">
                                {event.title}
                              </h3>
                              <div className="flex space-x-1" data-id="element-1733">
                                {event.verified && <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md text-xs" data-id="element-1734">
                                    Verified
                                  </span>}
                                {event.trending && <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md text-xs" data-id="element-1735">
                                    Trending
                                  </span>}
                                <button onClick={() => toggleSaveEvent(event.id)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 transform hover:scale-110" data-id="element-1736">
                                  <BookmarkIcon className={`w-4 h-4 ${savedEvents.includes(event.id) ? 'fill-gray-700 dark:fill-gray-300 text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'} transition-colors duration-300`} data-id="element-1737" />
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1 mb-2" data-id="element-1738">
                              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-xs dark:text-gray-200" data-id="element-1739">
                                {event.category}
                              </span>
                              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-xs dark:text-gray-200" data-id="element-1740">
                                {event.type}
                              </span>
                              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-xs dark:text-gray-200" data-id="element-1741">
                                {event.venue}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1742">
                              {event.description}
                            </p>
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mb-1" data-id="element-1743">
                              <CalendarIcon className="w-4 h-4 mr-1" data-id="element-1744" />
                              <span data-id="element-1745">{event.date}</span>
                              <span className="mx-2" data-id="element-1746">•</span>
                              <ClockIcon className="w-4 h-4 mr-1" data-id="element-1747" />
                              <span data-id="element-1748">{event.time}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1749">
                              <MapPinIcon className="w-4 h-4 mr-1" data-id="element-1750" />
                              <span data-id="element-1751">{event.location}</span>
                            </div>
                            <div className="text-sm mb-3 flex items-center justify-between" data-id="element-1752">
                              <div data-id="element-1753">
                                <span className="font-medium dark:text-gray-200" data-id="element-1754">
                                  Price:{' '}
                                </span>
                                <span className="text-gray-600 dark:text-gray-300" data-id="element-1755">
                                  {event.price}
                                </span>
                              </div>
                              {event.price !== 'Free' && event.price !== 'Free. Check event page' && <Link to="/checkout" className="px-3 py-1 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md text-sm transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-105" data-id="element-1756">
                                    Buy Ticket
                                  </Link>}
                            </div>
                            <div className="flex flex-wrap gap-1 mb-3" data-id="element-1757">
                              {event.tags.map((tag, index) => <span key={index} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-xs dark:text-gray-200" data-id="element-1758">
                                  {tag}
                                </span>)}
                            </div>
                            <div className="flex justify-between items-center" data-id="element-1759">
                              <div className="flex space-x-2" data-id="element-1760">
                                <Link to={`/event/${event.id}`} className="px-3 py-1 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md text-sm transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-105" data-id="element-1761">
                                  View Details
                                </Link>
                                <DropdownMenu trigger={<button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm flex items-center dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1763">
                                      <CalendarIcon className="w-3 h-3 mr-1" data-id="element-1764" />
                                      Add to Calendar
                                    </button>} data-id="element-1762">
                                  <DropdownMenu.Item label="Google Calendar" onClick={() => addToCalendar(event, 'google')} data-id="element-1765" />
                                  <DropdownMenu.Item label="Outlook" onClick={() => addToCalendar(event, 'outlook')} data-id="element-1766" />
                                  <DropdownMenu.Item label="Yahoo Calendar" onClick={() => addToCalendar(event, 'yahoo')} data-id="element-1767" />
                                  <DropdownMenu.Item label="iCal Download" onClick={() => addToCalendar(event, 'ical')} data-id="element-1768" />
                                </DropdownMenu>
                                <DropdownMenu trigger={<button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1770">
                                      <Share2Icon className="w-3 h-3" data-id="element-1771" />
                                    </button>} data-id="element-1769">
                                  <DropdownMenu.Item label="Share on Twitter" icon={<TwitterIcon className="w-4 h-4" data-id="element-1773" />} onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${event.title} on JoinToday.co.ke!`)}&url=${encodeURIComponent(`https://jointoday.co.ke/event/${event.id}`)}`, '_blank')} data-id="element-1772" />
                                  <DropdownMenu.Item label="Share on Facebook" icon={<FacebookIcon className="w-4 h-4" data-id="element-1775" />} onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://jointoday.co.ke/event/${event.id}`)}&quote=${encodeURIComponent(`Check out ${event.title} on JoinToday.co.ke!`)}`, '_blank')} data-id="element-1774" />
                                  <DropdownMenu.Item label="Share on WhatsApp" icon={<span className="text-sm" data-id="element-1777">📱</span>} onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${event.title} on JoinToday.co.ke! https://jointoday.co.ke/event/${event.id}`)}`, '_blank')} data-id="element-1776" />
                                  <DropdownMenu.Item label="Copy Link" icon={<LinkIcon className="w-4 h-4" data-id="element-1779" />} onClick={() => copyToClipboard(`https://jointoday.co.ke/event/${event.id}`)} data-id="element-1778" />
                                </DropdownMenu>
                              </div>
                              <a href={event.ticketUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-300" data-id="element-1780">
                                <span className="mr-1" data-id="element-1781">
                                  via {event.organizer}
                                </span>
                                <ExternalLinkIcon className="w-3 h-3" data-id="element-1782" />
                              </a>
                            </div>
                          </div>
                        </div>)}
                  </div>
                </div>
              </>}
            {linkCopied && <div className="fixed bottom-4 right-4 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 px-4 py-2 rounded-md flex items-center z-50 animate-fadeIn" data-id="element-1783">
                <CheckIcon className="w-4 h-4 mr-2" data-id="element-1784" />
                <span data-id="element-1785">Link copied to clipboard</span>
              </div>}
          </>}
        {activeTab === 'monetization' && <div className="py-4" data-id="element-1786">
            <div className="mb-8" data-id="element-1787">
              <h2 className="text-2xl font-bold mb-4 dark:text-gray-200" data-id="element-1788">
                Event Ticketing Solutions
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-1789">
                Streamline your event management with our comprehensive
                ticketing platform. Create, sell, and manage tickets for your
                events with ease.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1790">
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md transform hover:scale-[1.02]" data-id="element-1791">
                  <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 hover:scale-110" data-id="element-1792">
                    <TicketIcon className="w-8 h-8 text-gray-600 dark:text-gray-300" data-id="element-1793" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 dark:text-gray-200" data-id="element-1794">
                    Ticket Management
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1795">
                    Create multiple ticket types with customizable pricing,
                    availability, and features.
                  </p>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md transform hover:scale-[1.02]" data-id="element-1796">
                  <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 hover:scale-110" data-id="element-1797">
                    <DollarSignIcon className="w-8 h-8 text-gray-600 dark:text-gray-300" data-id="element-1798" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 dark:text-gray-200" data-id="element-1799">
                    Secure Payments
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1800">
                    Accept payments via M-Pesa, credit cards, and other popular
                    payment methods.
                  </p>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md transform hover:scale-[1.02]" data-id="element-1801">
                  <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 hover:scale-110" data-id="element-1802">
                    <BarChartIcon className="w-8 h-8 text-gray-600 dark:text-gray-300" data-id="element-1803" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 dark:text-gray-200" data-id="element-1804">
                    Real-time Analytics
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1805">
                    Track sales, attendance, and customer demographics with
                    powerful reporting tools.
                  </p>
                </div>
              </div>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden mb-8 transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md" data-id="element-1806">
                <div className="p-6" data-id="element-1807">
                  <h3 className="text-xl font-bold mb-4 dark:text-gray-200" data-id="element-1808">
                    Monetization Options for Event Organizers
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1809">
                    <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md transform hover:scale-[1.01]" data-id="element-1810">
                      <h4 className="font-bold mb-2 dark:text-gray-200" data-id="element-1811">
                        Become an Event Organizer
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1812">
                        Create and sell tickets to your own events
                      </p>
                      <ul className="text-sm space-y-2 mb-4" data-id="element-1813">
                        <li className="flex items-center" data-id="element-1814">
                          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1815">
                            <span className="text-xs" data-id="element-1816">✓</span>
                          </div>
                          <span className="dark:text-gray-200" data-id="element-1817">
                            Create unlimited events
                          </span>
                        </li>
                        <li className="flex items-center" data-id="element-1818">
                          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1819">
                            <span className="text-xs" data-id="element-1820">✓</span>
                          </div>
                          <span className="dark:text-gray-200" data-id="element-1821">
                            Customizable event pages
                          </span>
                        </li>
                        <li className="flex items-center" data-id="element-1822">
                          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1823">
                            <span className="text-xs" data-id="element-1824">✓</span>
                          </div>
                          <span className="dark:text-gray-200" data-id="element-1825">
                            Real-time sales tracking
                          </span>
                        </li>
                      </ul>
                      <Link to="/seller-info" className="block text-center w-full border border-gray-300 dark:border-gray-600 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md py-2 text-sm transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-[1.02]" data-id="element-1826">
                        Sign up as an organizer
                      </Link>
                    </div>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md transform hover:scale-[1.01]" data-id="element-1827">
                      <h4 className="font-bold mb-2 dark:text-gray-200" data-id="element-1828">
                        Become an Event Influencer
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1829">
                        Promote events and earn commission on ticket sales
                      </p>
                      <ul className="text-sm space-y-2 mb-4" data-id="element-1830">
                        <li className="flex items-center" data-id="element-1831">
                          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1832">
                            <span className="text-xs" data-id="element-1833">✓</span>
                          </div>
                          <span className="dark:text-gray-200" data-id="element-1834">
                            Unique referral links
                          </span>
                        </li>
                        <li className="flex items-center" data-id="element-1835">
                          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1836">
                            <span className="text-xs" data-id="element-1837">✓</span>
                          </div>
                          <span className="dark:text-gray-200" data-id="element-1838">
                            5-10% commission per sale
                          </span>
                        </li>
                        <li className="flex items-center" data-id="element-1839">
                          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1840">
                            <span className="text-xs" data-id="element-1841">✓</span>
                          </div>
                          <span className="dark:text-gray-200" data-id="element-1842">
                            Promotional materials provided
                          </span>
                        </li>
                      </ul>
                      <Link to="/seller-info" className="block text-center w-full border border-gray-300 dark:border-gray-600 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md py-2 text-sm transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-[1.02]" data-id="element-1843">
                        Sign up as an influencer
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 mb-8 transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md" data-id="element-1844">
                <h3 className="text-xl font-bold mb-4 dark:text-gray-200" data-id="element-1845">
                  Partner with JoinToday.co.ke
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-1846">
                  Are you a venue owner or service provider for events? Partner
                  with us to reach more clients.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1847">
                  <div data-id="element-1848">
                    <h4 className="font-bold mb-2 dark:text-gray-200" data-id="element-1849">
                      For Venues
                    </h4>
                    <ul className="text-sm space-y-2" data-id="element-1850">
                      <li className="flex items-center" data-id="element-1851">
                        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1852">
                          <span className="text-xs" data-id="element-1853">✓</span>
                        </div>
                        <span className="dark:text-gray-200" data-id="element-1854">
                          List your venue in our directory
                        </span>
                      </li>
                      <li className="flex items-center" data-id="element-1855">
                        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1856">
                          <span className="text-xs" data-id="element-1857">✓</span>
                        </div>
                        <span className="dark:text-gray-200" data-id="element-1858">
                          Get booking inquiries
                        </span>
                      </li>
                      <li className="flex items-center" data-id="element-1859">
                        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1860">
                          <span className="text-xs" data-id="element-1861">✓</span>
                        </div>
                        <span className="dark:text-gray-200" data-id="element-1862">
                          Showcase your space with photos
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div data-id="element-1863">
                    <h4 className="font-bold mb-2 dark:text-gray-200" data-id="element-1864">
                      For Service Providers
                    </h4>
                    <ul className="text-sm space-y-2" data-id="element-1865">
                      <li className="flex items-center" data-id="element-1866">
                        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1867">
                          <span className="text-xs" data-id="element-1868">✓</span>
                        </div>
                        <span className="dark:text-gray-200" data-id="element-1869">
                          Catering, decor, sound, lighting, etc.
                        </span>
                      </li>
                      <li className="flex items-center" data-id="element-1870">
                        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1871">
                          <span className="text-xs" data-id="element-1872">✓</span>
                        </div>
                        <span className="dark:text-gray-200" data-id="element-1873">
                          Connect with event organizers
                        </span>
                      </li>
                      <li className="flex items-center" data-id="element-1874">
                        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mr-2" data-id="element-1875">
                          <span className="text-xs" data-id="element-1876">✓</span>
                        </div>
                        <span className="dark:text-gray-200" data-id="element-1877">
                          Build your service portfolio
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                <Link to="/seller-info" className="block text-center w-full border border-gray-300 dark:border-gray-600 rounded-md py-2 text-sm dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700 transform hover:scale-[1.01]" data-id="element-1878">
                  Learn more about partnerships
                </Link>
              </div>
              <div className="text-center" data-id="element-1879">
                <h3 className="text-xl font-bold mb-4 dark:text-gray-200" data-id="element-1880">
                  Ready to Get Started?
                </h3>
                <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1881">
                  <Link to="/seller-info" className="bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md px-6 py-3 transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-105" data-id="element-1882">
                    View All Monetization Options
                  </Link>
                  <Link to="/register?type=seller" className="border border-gray-300 dark:border-gray-600 rounded-md px-6 py-3 dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700 transform hover:scale-105" data-id="element-1883">
                    Create Partner Account
                  </Link>
                </div>
              </div>
            </div>
          </div>}
      </div>
      {/* Create Event Modal */}
      {showCreateEventModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn" data-id="element-1884">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-id="element-1885">
            <div className="flex justify-between items-center mb-4" data-id="element-1886">
              <h3 className="text-lg font-bold dark:text-gray-200" data-id="element-1887">
                Create an Event
              </h3>
              <button onClick={() => setShowCreateEventModal(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 transform hover:scale-110" data-id="element-1888">
                <XIcon className="w-5 h-5 dark:text-gray-200" data-id="element-1889" />
              </button>
            </div>
            <div className="mb-4" data-id="element-1890">
              <div className="flex space-x-2 mb-4" data-id="element-1891">
                <button className="flex-1 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md py-2 text-sm flex items-center justify-center transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-[1.02]" data-id="element-1892">
                  <UserIcon className="w-4 h-4 mr-2" data-id="element-1893" />
                  Event Organizer
                </button>
                <button className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md py-2 text-sm flex items-center justify-center dark:text-gray-200 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-700 transform hover:scale-[1.02]" data-id="element-1894">
                  <UserPlusIcon className="w-4 h-4 mr-2" data-id="element-1895" />
                  Event Influencer
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4" data-id="element-1896">
                Create and manage your own events on JoinToday.co.ke. Fill out
                the form below to get started.
              </p>
            </div>
            <form onSubmit={handleCreateEventSubmit} data-id="element-1897">
              <div className="mb-4" data-id="element-1898">
                <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1899">
                  Event Title
                </label>
                <input type="text" name="title" value={eventFormData.title} onChange={handleEventFormChange} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" required data-id="element-1900" />
              </div>
              <div className="mb-4" data-id="element-1901">
                <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1902">
                  Description
                </label>
                <textarea name="description" value={eventFormData.description} onChange={handleEventFormChange} rows={3} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" required data-id="element-1903"></textarea>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1904">
                <div data-id="element-1905">
                  <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1906">
                    Date
                  </label>
                  <input type="date" name="date" value={eventFormData.date} onChange={handleEventFormChange} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" required data-id="element-1907" />
                </div>
                <div data-id="element-1908">
                  <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1909">
                    Time
                  </label>
                  <input type="time" name="time" value={eventFormData.time} onChange={handleEventFormChange} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" required data-id="element-1910" />
                </div>
              </div>
              <div className="mb-4" data-id="element-1911">
                <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1912">
                  Location
                </label>
                <input type="text" name="location" value={eventFormData.location} onChange={handleEventFormChange} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" required data-id="element-1913" />
              </div>
              <div className="mb-4" data-id="element-1914">
                <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1915">
                  Category
                </label>
                <select name="category" value={eventFormData.category} onChange={handleEventFormChange} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" required data-id="element-1916">
                  <option value="" data-id="element-1917">Select a category</option>
                  <option value="entertainment" data-id="element-1918">Entertainment</option>
                  <option value="business" data-id="element-1919">Business & Networking</option>
                  <option value="health" data-id="element-1920">Health & Fitness</option>
                  <option value="education" data-id="element-1921">Education</option>
                  <option value="arts" data-id="element-1922">Arts & Culture</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1923">
                <div data-id="element-1924">
                  <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1925">
                    Ticket Price (KES)
                  </label>
                  <input type="number" name="ticketPrice" value={eventFormData.ticketPrice} onChange={handleEventFormChange} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" placeholder="0 for free events" data-id="element-1926" />
                </div>
                <div data-id="element-1927">
                  <label className="block text-sm font-medium mb-1 dark:text-gray-300" data-id="element-1928">
                    Max Attendees
                  </label>
                  <input type="number" name="maxAttendees" value={eventFormData.maxAttendees} onChange={handleEventFormChange} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-200 transition-all duration-300 focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500" data-id="element-1929" />
                </div>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-gray-50 dark:bg-gray-700 mb-4" data-id="element-1930">
                <h4 className="text-sm font-medium mb-2 dark:text-gray-200" data-id="element-1931">
                  Event Creator Benefits
                </h4>
                <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1 list-disc list-inside" data-id="element-1932">
                  <li data-id="element-1933">Reach thousands of potential attendees</li>
                  <li data-id="element-1934">Easy ticket management and sales</li>
                  <li data-id="element-1935">Promotion across our platform and social media</li>
                  <li data-id="element-1936">Real-time analytics and attendee insights</li>
                  <li data-id="element-1937">15% commission on ticket sales (standard rate)</li>
                </ul>
              </div>
              <button type="submit" className="w-full bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md py-2 text-sm transition-all duration-300 hover:bg-gray-700 dark:hover:bg-gray-300 transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-1938">
                Submit Event for Review
              </button>
            </form>
          </div>
        </div>}
    </div>;
};
export default Events;